import { KeyValue } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { FormControl, NgModel } from '@angular/forms';
import { groupOptionsByHeading } from 'common/helper';
import { DetailInfoFormStore } from 'shared/shared.helper';
import { DetailInfoTypeComponent } from '../detail-info-type.component';

type Option = KeyValue<string, string> & { heading?: string };

@Component({
  selector: 'detail-info-select-with-text',
  templateUrl: './detail-info-select-with-text.component.html',
  standalone: false
})
export class DetailInfoSelectWithTextComponent
  extends DetailInfoTypeComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('commentBox') commentBox!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('selectWithTextModel') selectWithTextModel!: NgModel;

  public options: ReadonlyArray<Option> = [];
  public optionGroups: { heading: string | null; options: Option[] }[] = [];

  public showComment = false;
  public dataStringValue = '';
  public dataStringExtraValue = '';
  public dataKey = '';

  private subs = new Subscription();
  private cdRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    super.ngOnInit();

    const raw = this.detailInfo?.inputContentDatas ?? [];

    this.options = Array.isArray(raw)
      ? (raw as Option[])
      : Object.entries(raw).map(([key, value]) => ({
          key: String(key),
          value: String(value)
        }));

    // Same optgroup grouping as the plain dropdown (type 8) - the with-comment
    // variant (8192) carries identical option data, headings included. The
    // legacy-dictionary fallback has no headings and yields one plain group.
    this.optionGroups = groupOptionsByHeading(this.options, (o) => o.heading);

    this.subs.add(
      DetailInfoFormStore.touched$.subscribe(() => {
        // Guard BEFORE scheduling: queuing a microtask per emission is what let a
        // change-detection-driven touched$ emitter loop the zone forever (tick → emit →
        // microtask → tick — the page-freeze bug). Once the inner ngModel is touched
        // there is nothing left to do, so schedule nothing.
        if (!(this.formControlObj as FormControl).touched) return;
        if (this.selectWithTextModel?.control?.touched) return;
        queueMicrotask(() => {
          this.markNgModelAsTouched();
          this.cdRef.detectChanges();
        });
      })
    );

    this.cdRef.detectChanges();
    console.groupEnd();
  }

  ngAfterViewInit(): void {
    const val = (this.formControlObj as FormControl).value as {
      dataStringValue?: string;
      dataStringExtraValue?: string;
    } | null;

    this.dataStringValue = (val?.dataStringValue ?? '').toString().trim();
    this.dataStringExtraValue = (val?.dataStringExtraValue ?? '')
      .toString()
      .trim();
    this.showComment = !!this.dataStringExtraValue;

    this.dataKey =
      this.options.find(
        (o) => String(o.key).trim() === String(this.dataStringValue)
      )?.key ?? '';
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subs.unsubscribe();
  }

  public selectOption(): void {
    const optionControl = this.formControlObj as FormControl;
    optionControl.setValue({
      dataStringValue: this.dataStringValue,
      dataStringExtraValue: this.dataStringExtraValue
    });
  }

  public toggleShowComment(): void {
    this.showComment = !this.showComment;

    if (this.showComment) {
      setTimeout(() => this.commentBox?.nativeElement?.focus(), 100);
    } else {
      this.dataStringExtraValue = '';
      this.selectOption();
      this.showComment = false;
    }
  }

  public trackByKey(_: number, o: { key: string }) {
    return o?.key;
  }

  private markNgModelAsTouched(): void {
    if (
      this.selectWithTextModel?.control &&
      !this.selectWithTextModel.control.touched
    ) {
      this.selectWithTextModel.control.markAsTouched();
    }
  }
}
